/**
 * URLs that carry a free prompt generator result into signup and back out.
 *
 * The free tool at `/free-ai-visibility-prompt-generator` offers "Track these
 * personas": sign up, then import the generation's prompts and persona drafts
 * into a property. The generation id travels as a PATH SEGMENT of the claim
 * route, not as a query parameter, because the auth middleware forwards only
 * the pathname when it bounces an unonboarded user to onboarding
 * (`/app/onboarding?redirectTo=<pathname>`); a `?generation=` query would be
 * dropped on the way.
 *
 * The marketing UI and the results email both build the CTA from here, so
 * the two can never disagree about the format the claim route consumes.
 *
 * Pure string helpers: safe for `@repo/core/client`.
 */

/** The signed-in claim route; the generation id is the next path segment. */
export const PROMPT_GENERATOR_CLAIM_PATH_PREFIX = "/app/prompt-generator-claim";

/** Where the free tool lives (English only; unprefixed paths resolve to EN). */
export const PROMPT_GENERATOR_PAGE_PATH =
	"/free-ai-visibility-prompt-generator";

/** The query parameter the free tool restores a result from. */
export const PROMPT_GENERATOR_RESULT_PARAM = "generation";

function trimTrailingSlashes(base: string): string {
	return base.replace(/\/+$/, "");
}

function withBase(path: string, absoluteBase?: string | null): string {
	const base = absoluteBase ? trimTrailingSlashes(absoluteBase.trim()) : "";
	return base ? `${base}${path}` : path;
}

/**
 * `/app/prompt-generator-claim/<generationId>`: the path the claim phase
 * serves. Signup's `redirectTo` points here.
 */
export function promptGeneratorClaimPath(generationId: string): string {
	return `${PROMPT_GENERATOR_CLAIM_PATH_PREFIX}/${encodeURIComponent(generationId)}`;
}

/**
 * The generation id in a claim path, or null when `path` is not one. Accepts
 * the path with or without a trailing slash or query string.
 */
export function parsePromptGeneratorClaimPath(path: string): string | null {
	const pathname = path.split(/[?#]/)[0] ?? "";
	const prefix = `${PROMPT_GENERATOR_CLAIM_PATH_PREFIX}/`;
	if (!pathname.startsWith(prefix)) {
		return null;
	}
	const segment = trimTrailingSlashes(pathname.slice(prefix.length));
	if (!segment || segment.includes("/")) {
		return null;
	}
	try {
		return decodeURIComponent(segment);
	} catch {
		return null;
	}
}

/**
 * The "Track these personas" href:
 * `/auth/signup?redirectTo=%2Fapp%2Fprompt-generator-claim%2F<generationId>`.
 * Pass `absoluteBase` (e.g. NEXT_PUBLIC_SITE_URL) for an email link.
 */
export function promptGeneratorSignupUrl(
	generationId: string,
	options: { absoluteBase?: string | null } = {},
): string {
	const redirectTo = encodeURIComponent(
		promptGeneratorClaimPath(generationId),
	);
	return withBase(
		`/auth/signup?redirectTo=${redirectTo}`,
		options.absoluteBase,
	);
}

/**
 * The shareable result link:
 * `/free-ai-visibility-prompt-generator?generation=<generationId>`.
 */
export function promptGeneratorResultsUrl(
	generationId: string,
	options: { absoluteBase?: string | null } = {},
): string {
	return withBase(
		`${PROMPT_GENERATOR_PAGE_PATH}?${PROMPT_GENERATOR_RESULT_PARAM}=${encodeURIComponent(generationId)}`,
		options.absoluteBase,
	);
}

/* ------------------------------------------------------------------ */
/* Claim window, import URL, duplicate matching                        */
/* ------------------------------------------------------------------ */

/**
 * How long a generation can be imported ("claimed") after it was made. The
 * free tool's result link keeps working after that; only the import into a
 * property stops.
 */
export const PROMPT_GENERATION_CLAIM_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** When a generation made at `createdAt` stops being importable. */
export function promptGenerationClaimExpiresAt(createdAt: Date | string): Date {
	const made = createdAt instanceof Date ? createdAt : new Date(createdAt);
	return new Date(made.getTime() + PROMPT_GENERATION_CLAIM_DAYS * DAY_MS);
}

/**
 * True once a generation is past its claim window. An unparseable date
 * counts as expired (it can only come from a malformed row).
 */
export function isPromptGenerationClaimExpired(
	createdAt: Date | string,
	now: Date = new Date(),
): boolean {
	const expiresAt = promptGenerationClaimExpiresAt(createdAt).getTime();
	return Number.isNaN(expiresAt) || now.getTime() > expiresAt;
}

/**
 * The in-app import step for a generation on one property:
 * `/app/<orgSlug>/properties/<propertyId>/personas/import?generation=<id>`.
 */
export function promptGeneratorImportPath(input: {
	organizationSlug: string;
	propertyId: string;
	generationId: string;
}): string {
	return `/app/${encodeURIComponent(input.organizationSlug)}/properties/${encodeURIComponent(input.propertyId)}/personas/import?${PROMPT_GENERATOR_RESULT_PARAM}=${encodeURIComponent(input.generationId)}`;
}

/**
 * A prompt's text reduced to what makes two prompts "the same prompt word
 * for word": case, curly quotes, repeated whitespace and trailing
 * punctuation do not count. The import skips a generated prompt whose
 * normalized text the property already has.
 */
export function normalizePromptTextForDedupe(text: string): string {
	return text
		.normalize("NFKC")
		.toLowerCase()
		.replace(/[‘’]/g, "'")
		.replace(/[“”]/g, '"')
		.replace(/\s+/g, " ")
		.trim()
		.replace(/[\s?.!]+$/, "");
}
